import { classNames } from "@/utils/classNames"

type TagVariant = "h1" | "h2" | "h3" | "h4" | "h5" | "h6" | "p" | "span"

export type TypographyVariant =
  | "titleL"
  | "titleM"
  | "textM"
  | "textM-bold"
  | "textS"
  | "textS-bold"
  | "textXS"
  | "textXS-bold"
  | "textXS"
  | "textXXS"

export interface TypographyProps extends React.PropsWithChildren {
  as?: TagVariant
  variant: TypographyVariant
  className?: string
  dataTestId?: string
  style?: object
}

function getTypographyClassNames(variant: TypographyVariant): string {
  const variantClass = {
    titleL: "text-[35px]/[1.2]",
    titleM: "text-[25px]/[1.2]",
    textM: "text-[16px]/[1.2]",
    "textM-bold": "text-[16px]/[1.2] font-bold",
    textS: "text-[14px]/[1.2]",
    "textS-bold": "text-[14px]/[1.2] font-bold",
    textXS: "text-[12px]/[1.2]",
    "textXS-bold": "text-[12px]/[1.2] font-bold",
    textXXS: "text-[9px]/[1.2]",
  }[variant]

  return classNames(variantClass)
}

/**
 * General usage component for every kind of text
 * Provides style variants which are compliant to the design system
 * For any special cases, any classNames can be provided to the component to
 * overwrite any preset style.
 * @param as target HTML element as which should be rendered
 * @param className CSS classes string
 * @param variant defined typography variant
 * @param children children components
 * @returns typography component
 */
export const Typography: React.FC<TypographyProps> = ({
  as: Element = "p",
  className,
  variant,
  children,
  dataTestId,
  style,
}: TypographyProps) => {
  const variantClassNames = getTypographyClassNames(variant)
  const defaultClasses = "text-white"

  return (
    <Element
      className={classNames(defaultClasses, className, variantClassNames)}
      data-testid={dataTestId}
      style={style}
    >
      {children}
    </Element>
  )
}
